import { format } from 'date-fns';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { levelName } from '../data/level-text';
import type { Message, RanWith } from '../types';

import { AnswerDetailsPopover } from './AnswerDetailsPopover';
import { ProviderLogo } from './ModelMark';

export interface MessageMetaProps {
  message: Pick<Message, 'createdAt' | 'ran' | 'route'>;
  onAskInNewChat?: () => void;
}

export function MessageMeta({ message, onAskInNewChat }: MessageMetaProps) {
  const { t } = useTranslation('ai-cio');
  const { ran, route } = message;
  if (!ran && !route) return null;
  const model = route ? { label: route.modelUsed.label, group: route.modelUsed.group } : ran?.model;
  const date = new Date(message.createdAt);
  return (
    <div className="mb-5 flex items-center gap-1 text-xs text-muted-foreground">
      <span>{t('title', 'AI CIO')}</span>
      <span aria-hidden="true">·</span>
      <span>{Number.isNaN(date.getTime()) ? '' : format(date, 'HH:mm')}</span>
      {model && (
        <>
          <span aria-hidden="true">·</span>
          {route ? (
            <Suspense fallback={<PlainModel model={model} />}>
              <AnswerDetailsPopover route={route} onAskInNewChat={onAskInNewChat}>
                <span className="flex shrink-0 text-foreground">
                  <ProviderLogo group={model.group} />
                </span>
                <span className="truncate">{model.label}</span>
              </AnswerDetailsPopover>
            </Suspense>
          ) : (
            <PlainModel model={model} />
          )}
        </>
      )}
      {ran?.level && (
        <>
          <span aria-hidden="true">·</span>
          <span>{levelName(t, ran.level.id, ran.level.label)}</span>
        </>
      )}
    </div>
  );
}

function PlainModel({ model }: { model: NonNullable<RanWith['model']> }) {
  return (
    <>
      <span className="ms-2 flex shrink-0 text-foreground">
        <ProviderLogo group={model.group} />
      </span>
      <span>{model.label}</span>
    </>
  );
}
